import { useEffect, useState } from 'react';
import { deleteWhitespaces } from 'utils/delete-whitespaces';
import { normalizeString } from 'utils/normalize-string';
import { parseHtml } from 'utils/parse-html';
import { TOCItem } from './types';

const htmlTagRegex = /(<([^>]+)>)/gi;

const createTOCItem = (
  innerHtml: string,
  nodeName: 'H2' | 'H3' | 'H4'
): TOCItem => {
  const innerHtmlWithoutTags = innerHtml.replace(htmlTagRegex, '');

  return {
    id: normalizeString(
      deleteWhitespaces(`${innerHtmlWithoutTags}`).toLocaleLowerCase()
    ),
    title: parseHtml(innerHtml, () => {}, {}),
    items: [],
    node: nodeName,
  };
};

const getContentStructure = (headings: Element[]): TOCItem[] => {
  let structuredHeadings: TOCItem[] = [];
  let h2id = '';
  let h3id = '';

  headings.forEach(({ nodeName, innerHTML }) => {
    // h2
    if (nodeName === 'H2') {
      const h2 = createTOCItem(innerHTML, 'H2');

      structuredHeadings = [...structuredHeadings, h2];

      h2id = normalizeString(h2.id);
    }

    // h3
    if (nodeName === 'H3') {
      const h3 = createTOCItem(innerHTML, 'H3');

      h3id = normalizeString(h3.id);

      const h2index = structuredHeadings.findIndex((h) => h.id === h2id);

      if (h2index !== -1) {
        structuredHeadings[h2index]?.items.push(h3);
      }
    }

    // h4
    if (nodeName === 'H4') {
      const h4 = createTOCItem(innerHTML, 'H4');

      const h2index = structuredHeadings.findIndex((h) => h.id === h2id);
      const h3index = structuredHeadings[h2index].items.findIndex(
        (h) => h.id === h3id
      );

      if (h2index !== -1 && h3index !== -1) {
        structuredHeadings[h2index]?.items[h3index]?.items.push(h4);
      }
    }
  });

  return structuredHeadings;
};

export const useTOC = ({ content }: { content: JSX.Element }) => {
  const [headings, setHeadings] = useState<Element[]>([]);

  const guideElement = document.getElementById('guide');

  useEffect(() => {
    if (guideElement) {
      setHeadings(Array.from(guideElement.querySelectorAll('h2, h3, h4')));
    }
  }, [content, guideElement]);

  const contentStructure = getContentStructure(headings);

  return contentStructure;
};
